import { create } from 'zustand';
import { Table, Order, MenuItem, OrderItem } from '../types';
import { v4 as uuidv4 } from 'uuid';
import { generateMockData } from '../utils/mockData';

interface Store {
  tables: Table[];
  menuItems: MenuItem[];
  currentTableId: string | null;
  currentOrderId: string | null;
  searchQuery: string;
  
  setSearchQuery: (query: string) => void;
  setCurrentTable: (tableId: string | null) => void;
  setCurrentOrder: (orderId: string | null) => void;
  
  addItemToOrder: (tableId: string, menuItem: MenuItem, quantity: number, notes?: string) => void;
  removeItemFromOrder: (orderId: string, itemId: string) => void;
  updateItemQuantity: (orderId: string, itemId: string, quantity: number) => void;
  
  addNoteToTable: (tableId: string, note: string) => void;
  addNoteToOrder: (orderId: string, note: string) => void;
  
  processPayment: (orderId: string, method: 'cash' | 'card', paidItems?: { item: OrderItem; quantity: number }[]) => void;
  splitBill: (orderId: string, ways: number) => Order[];
  
  moveTableForward: (tableId: string) => void;
  moveTableBackward: (tableId: string) => void;
  clearTable: (tableId: string) => void;
  updateReservation: (tableId: string, reservation: Table['reservation']) => void;
  
  initializeData: () => void;
}

export const useStore = create<Store>((set, get) => ({
  tables: [],
  menuItems: [],
  currentTableId: null,
  currentOrderId: null,
  searchQuery: '',
  
  setSearchQuery: (query) => set({ searchQuery: query }),
  setCurrentTable: (tableId) => set({ currentTableId: tableId }),
  setCurrentOrder: (orderId) => set({ currentOrderId: orderId }),
  
  addItemToOrder: (tableId, menuItem, quantity, notes) => {
    set((state) => {
      const table = state.tables.find(t => t.id === tableId);
      if (!table) return state;
      
      let activeOrder = table.orders.find(o => o.status === 'pending');
      
      if (!activeOrder) {
        activeOrder = {
          id: uuidv4(),
          tableId,
          items: [],
          status: 'pending',
          createdAt: new Date(),
          notes: '',
          total: 0
        };
        table.orders.push(activeOrder);
      }
      
      const existingItem = activeOrder.items.find(i => i.menuItemId === menuItem.id && i.notes === notes);
      
      if (existingItem) {
        existingItem.quantity += quantity;
      } else {
        activeOrder.items.push({
          id: uuidv4(),
          menuItemId: menuItem.id,
          name: menuItem.name,
          translationKey: menuItem.translationKey,
          quantity,
          price: menuItem.price,
          notes
        });
      }
      
      activeOrder.total = activeOrder.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
      
      const newTables = [...state.tables];
      const tableIndex = newTables.findIndex(t => t.id === tableId);
      newTables[tableIndex] = { ...table };
      
      if (table.status === 'free') {
        newTables[tableIndex].status = 'awaiting-order';
      }
      
      return { tables: newTables };
    });
  },
  
  removeItemFromOrder: (orderId, itemId) => {
    set((state) => {
      const newTables = state.tables.map(table => ({
        ...table,
        orders: table.orders.map(order => {
          if (order.id === orderId) {
            const newItems = order.items.filter(item => item.id !== itemId);
            const newTotal = newItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
            return { ...order, items: newItems, total: newTotal };
          }
          return order;
        })
      }));
      return { tables: newTables };
    });
  },
  
  updateItemQuantity: (orderId, itemId, quantity) => {
    set((state) => {
      const newTables = state.tables.map(table => ({
        ...table,
        orders: table.orders.map(order => {
          if (order.id === orderId) {
            const newItems = order.items.map(item => 
              item.id === itemId ? { ...item, quantity } : item
            );
            const newTotal = newItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
            return { ...order, items: newItems, total: newTotal };
          }
          return order;
        })
      }));
      return { tables: newTables };
    });
  },
  
  addNoteToTable: (tableId, note) => {
    set((state) => {
      const newTables = state.tables.map(table => 
        table.id === tableId ? { ...table, notes: note } : table
      );
      return { tables: newTables };
    });
  },
  
  addNoteToOrder: (orderId, note) => {
    set((state) => {
      const newTables = state.tables.map(table => ({
        ...table,
        orders: table.orders.map(order => 
          order.id === orderId ? { ...order, notes: note } : order
        )
      }));
      return { tables: newTables };
    });
  },
  
  processPayment: (orderId, method, paidItems) => {
    set((state) => {
      const newTables = state.tables.map(table => {
        const orderIndex = table.orders.findIndex(o => o.id === orderId);
        if (orderIndex !== -1) {
          const order = table.orders[orderIndex];
          
          // If paidItems is provided, we're doing a partial payment
          if (paidItems && paidItems.length > 0) {
            // Calculate remaining items after payment
            const remainingItems: OrderItem[] = [];
            
            order.items.forEach(orderItem => {
              const paidItem = paidItems.find(pi => pi.item.id === orderItem.id);
              
              if (paidItem) {
                // This item was partially or fully paid
                const remainingQty = orderItem.quantity - paidItem.quantity;
                if (remainingQty > 0) {
                  // Some quantity remains unpaid
                  remainingItems.push({
                    ...orderItem,
                    quantity: remainingQty
                  });
                }
                // If remainingQty <= 0, the item is fully paid and not added to remaining
              } else {
                // This item was not paid at all
                remainingItems.push(orderItem);
              }
            });
            
            // If there are remaining items, update the order
            if (remainingItems.length > 0) {
              const updatedOrder = {
                ...order,
                items: remainingItems,
                total: remainingItems.reduce((sum, item) => sum + (item.price * item.quantity), 0)
              };
              
              const newOrders = [...table.orders];
              newOrders[orderIndex] = updatedOrder;
              
              return {
                ...table,
                orders: newOrders,
                // Table remains in waiting-payment status if items remain
                status: 'waiting-payment' as Table['status']
              };
            } else {
              // All items paid, remove the order
              const newOrders = table.orders.filter(o => o.id !== orderId);
              return {
                ...table,
                orders: newOrders,
                status: newOrders.some(o => o.status === 'pending') ? 'waiting-payment' : 'free' as Table['status']
              };
            }
          } else {
            // Full payment - remove entire order
            const newOrders = table.orders.filter(o => o.id !== orderId);
            return { 
              ...table, 
              orders: newOrders,
              status: newOrders.some(o => o.status === 'pending') ? 'waiting-payment' : 'free' as Table['status']
            };
          }
        }
        return table;
      });
      return { tables: newTables };
    });
  },
  
  splitBill: (orderId, ways) => {
    const state = get();
    let order: Order | undefined;
    
    for (const table of state.tables) {
      order = table.orders.find(o => o.id === orderId);
      if (order) break;
    }
    
    if (!order) return [];
    
    const splitOrders: Order[] = [];
    const amountPerPerson = order.total / ways;
    
    for (let i = 0; i < ways; i++) {
      splitOrders.push({
        ...order,
        id: uuidv4(),
        total: amountPerPerson,
        notes: `Split ${i + 1}/${ways} from order ${order.id}`
      });
    }
    
    return splitOrders;
  },
  
  moveTableForward: (tableId) => {
    set((state) => {
      const newTables = state.tables.map(table => {
        if (table.id === tableId) {
          let newStatus: Table['status'] = table.status;
          
          if (table.status === 'free' && table.orders.some(o => o.status === 'pending')) {
            newStatus = 'awaiting-order';
          } else if (table.status === 'awaiting-order') {
            newStatus = 'waiting-payment';
          }
          // waiting-payment is the final status before processing payment
          
          return { ...table, status: newStatus };
        }
        return table;
      });
      return { tables: newTables };
    });
  },
  
  moveTableBackward: (tableId) => {
    set((state) => {
      const newTables = state.tables.map(table => {
        if (table.id === tableId) {
          let newStatus: Table['status'] = table.status;
          
          if (table.status === 'waiting-payment') {
            newStatus = 'awaiting-order';
          } else if (table.status === 'awaiting-order') {
            newStatus = 'free';
          }
          
          return { ...table, status: newStatus };
        }
        return table;
      });
      return { tables: newTables };
    });
  },
  
  clearTable: (tableId) => {
    set((state) => {
      const newTables = state.tables.map(table => {
        if (table.id === tableId) {
          return { 
            ...table, 
            status: 'free' as Table['status'],
            orders: [],
            notes: ''
          };
        }
        return table;
      });
      return { tables: newTables };
    });
  },
  
  updateReservation: (tableId, reservation) => {
    set((state) => {
      const newTables = state.tables.map(table => {
        if (table.id === tableId) {
          return { 
            ...table, 
            reservation
          };
        }
        return table;
      });
      return { tables: newTables };
    });
  },
  
  initializeData: () => {
    const { tables, menuItems } = generateMockData();
    set({ tables, menuItems });
  }
}));